import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Isvg from 'react-inlinesvg';

import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import Segment from 'helpers/segment';
import { intlMessage } from 'constants/propTypes';
import Message from 'components/Message';
import { selectUserInfo } from 'selectors/general';
import messages from '../ReservationFeedbackModalV2.messages';

import './GuidancePanelContent.scss';

const { element } = bem('guidance-panel-content');

const GuidancePanelContentDescription = ({ description }) => {
  const descriptions = Array.isArray(description) ? description : [description];
  const hasMultipleItems = descriptions.length > 1;

  if (hasMultipleItems) {
    return (
      <ul className={element('guideline-list')}>
        {descriptions.map((desc, index) => (
          <li key={desc?.id || `guideline-${index}`}>
            <Message message={desc} />
          </li>
        ))}
      </ul>
    );
  }

  return <Message as="div" className={element('guideline-description')} message={descriptions[0]} />;
};

GuidancePanelContentDescription.propTypes = {
  description: PropTypes.oneOfType([intlMessage, PropTypes.arrayOf(intlMessage)]).isRequired,
};

const handleLearnMoreClick = (venueId, reservationId) => {
  if (venueId != null && reservationId != null) {
    Segment.event('Click Report Learn More', { VID: venueId, RID: reservationId });
  }
};

const GuidancePanelContent = ({ descriptionMessage, guidelines, venueId, reservationId }) => {
  const { locale } = useSelector(selectUserInfo);

  return (
    <div className={element('guidance-panel-content')}>
      {descriptionMessage && <Message className={element('guidance-panel-description')} message={descriptionMessage} />}
      {guidelines.map(guideline => (
        <div key={guideline.title.id} className={element('guideline')}>
          <div className={element('guideline-icon-wrapper')}>
            <Isvg
              className={element('guideline-icon', guideline.iconModifier && { [guideline.iconModifier]: true })}
              src={guideline.icon}
            />
          </div>
          <div className={element('guideline-text')}>
            <Message as="div" className={element('guideline-title')} message={guideline.title} />
            <div className={element('guideline-body')}>
              <GuidancePanelContentDescription description={guideline.description} />
              {guideline.descriptionFooter && (
                <Message
                  as="div"
                  className={element('guideline-description-footer')}
                  message={guideline.descriptionFooter}
                />
              )}
            </div>
          </div>
        </div>
      ))}
      <div className={element('guidance-panel-learn-more')}>
        <a
          href={makePath.reportUserFAQs(locale)}
          target="_blank"
          rel="noopener noreferrer"
          className={element('learn-more-link')}
          onClick={() => handleLearnMoreClick(venueId, reservationId)}>
          <Message message={messages.learnMore} />
        </a>
      </div>
    </div>
  );
};

GuidancePanelContent.propTypes = {
  descriptionMessage: intlMessage,
  guidelines: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.oneOfType([intlMessage, PropTypes.arrayOf(intlMessage)]).isRequired,
      descriptionFooter: intlMessage,
      icon: PropTypes.string.isRequired,
      iconModifier: PropTypes.string,
      title: intlMessage.isRequired,
    })
  ).isRequired,
  venueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reservationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default GuidancePanelContent;
